import { useState } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { listCountries, listAreaCodeGroups } from "../api/geography";

const PAGE_SIZE = 100;

function AreaCodeGroupsPanel() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["area-code-groups"],
    queryFn: listAreaCodeGroups,
  });
  if (isLoading) return <p className="text-gray-500">Loading…</p>;
  if (isError) return <p className="text-red-600">{(error as Error).message}</p>;
  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-left text-gray-500">
          <tr>
            <th className="px-4 py-2">Group</th>
            <th className="px-4 py-2">Area codes</th>
            <th className="px-4 py-2">Description</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {(data ?? []).map((g) => (
            <tr key={g.group_id}>
              <td className="px-4 py-2 font-medium">{g.name}</td>
              <td className="px-4 py-2 font-mono text-gray-600">{g.area_codes}</td>
              <td className="px-4 py-2 text-gray-600">{g.description || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CountriesPanel() {
  const [page, setPage] = useState(1);
  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ["countries", page],
    queryFn: () => listCountries(page, PAGE_SIZE),
    placeholderData: keepPreviousData,
  });
  if (isLoading) return <p className="text-gray-500">Loading…</p>;
  if (isError) return <p className="text-red-600">{(error as Error).message}</p>;
  const totalPages = data ? Math.max(1, Math.ceil(data.total / (data.per_page || PAGE_SIZE))) : 1;
  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="px-4 py-2 text-xs text-gray-500 border-b border-gray-100">
        {isFetching ? "Loading…" : `${data?.total ?? 0} countries`}
      </div>
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-left text-gray-500">
          <tr>
            <th className="px-4 py-2">Country</th>
            <th className="px-4 py-2">ISO</th>
            <th className="px-4 py-2">Dialing code</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {(data?.data ?? []).map((c) => (
            <tr key={c.country_id}>
              <td className="px-4 py-2 font-medium">{c.name}</td>
              <td className="px-4 py-2 text-gray-600">{c.iso_code_2 || "—"}</td>
              <td className="px-4 py-2 font-mono text-gray-600">{c.dialing_code || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex items-center justify-between px-4 py-2 border-t border-gray-100 text-sm">
        <button
          disabled={page <= 1}
          onClick={() => setPage((p) => p - 1)}
          className="px-3 py-1 rounded bg-gray-100 hover:bg-gray-200 disabled:opacity-40"
        >
          Previous
        </button>
        <span className="text-gray-500">Page {page} of {totalPages}</span>
        <button
          disabled={page >= totalPages}
          onClick={() => setPage((p) => p + 1)}
          className="px-3 py-1 rounded bg-gray-100 hover:bg-gray-200 disabled:opacity-40"
        >
          Next
        </button>
      </div>
    </div>
  );
}

export function GeographyPage() {
  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold mb-4">Area-Code Groups</h1>
        <AreaCodeGroupsPanel />
      </div>
      <div>
        <h2 className="text-xl font-semibold mb-3">Countries</h2>
        <CountriesPanel />
      </div>
    </div>
  );
}
